import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Breadcrumbs } from '@uhg-abyss/web/ui/Breadcrumbs';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { PageBody } from '@uhg-abyss/web/ui/PageBody';
import { Router } from '@uhg-abyss/web/ui/Router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isEmpty } from 'lodash';
import find from 'lodash/find';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { CardWrapper } from '../../common/CardWrapper';
import { Constants, DEFAULT_EMPTY_ARRAY } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import GuestFooter from '../../common/GuestFooter/GuestFooter';
import GuestHeader from '../../common/GuestHeader/GuestHeader';
import { LoadingSpinnerComp } from '../../common/Loader';
import {
  useEducationalContentStatus,
  useSaveEducationalContentStatus,
} from '../../hooks/useEducationalContent';
import { useGetPlanDefinitions } from '../../hooks/useGetPlanDefinitions';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useGuestAcquisitionStore } from '../../store/useGuestAcquisitionStore';
import { GuestAcquisitionState } from '../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import { useGuestStore } from '../../store/useGuestStore';
import { GuestData, GuestState } from '../../store/useGuestStore/guestStore';
import { DateTime } from '../../utils/DateTime';
import { getIsIFPEarlyPay } from '../../utils/guest.utils';
import { Footer } from '../ProviderSearch/routes/Footer';
import {
  buildPortalData,
  filterAvailablePlansByPlanName,
  getAvailablePlans,
  setGuestPlanSelectionData,
} from './Guest.utils';
import {
  CHOOSE_PCP_ROUTES,
  DEFAULT_ROUTES,
  GuestRoute,
  PLAN_SELECTOR_ROUTES,
  SEARCH_RESULTS_ROUTES,
} from './GuestRoutesConstants';
import {
  getBlueGreenGQLApiUrl,
  isLawwGuest,
  setupPortalStateResponder,
} from './utils';

export const getGuestRoutes = (guestData: GuestData): GuestRoute[] => {
  const isIFPEarlyPay = getIsIFPEarlyPay(guestData);

  if (isIFPEarlyPay) {
    return [...DEFAULT_ROUTES, ...SEARCH_RESULTS_ROUTES, ...CHOOSE_PCP_ROUTES];
  }

  return [...PLAN_SELECTOR_ROUTES];
};

export const GuestContentWrapper = styled('div', {
  dynamic: ({ cssProps: { shouldHideOverflow } = {} }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    ...(shouldHideOverflow ? {} : { overflow: 'hidden' }),
    minHeight: 0,
  }),
});

const RoutesScrollableWrapper = styled('div', {
  dynamic: ({ cssProps: { shouldHideOverflow } = {} }) => ({
    flex: 1,
    ...(shouldHideOverflow ? {} : { overflow: 'hidden auto' }),
    display: 'flex',
    flexDirection: 'column',
  }),
});

const GuestFooterWrapper = styled('div', {
  flexShrink: 0,
  flexGrow: 0,
  width: '100%',
});

export const GuestHeaderWrapper = ({
  backgroundColor = 'white',
  height = 'auto',
  children,
}) => (
  <Box
    color={backgroundColor}
    css={{
      'abyss-box-root': {
        backgroundColor,
        '@media (max-width: 744px)': {
          borderBottom: 'solid',
          borderColor: '$headerCardWrapperBorderColor',
        },
        borderBottom: 'solid 1px $core.color.neutral.20',
        padding: '24px',
        height,
      },
    }}
  >
    {children}
  </Box>
);

export const GuestRoutes = () => {
  const { navigate } = useRouter();
  const {
    guestData,
    chooseType,
    guestPlanSelectorData,
    setGuestPlanSelectorData,
  } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestData: state.guestData,
      chooseType: state.chooseType,
      guestPlanSelectorData: state.guestPlanSelectorData,
      setGuestPlanSelectorData: state.setGuestPlanSelectorData,
    }))
  );

  const { isNavigatingMaestro, requestData } = useGuestAcquisitionStore(
    useShallow((state: GuestAcquisitionState) => ({
      isNavigatingMaestro: state.isNavigatingMaestro,
      requestData: state.requestData,
    }))
  );

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue } = chipStore;

  const {
    delsysCode,
    coverageType: coverageTypeCode = '',
    isGuestDeeplinkFlow,
    planName,
    policyNumber,
    source,
    planYear,
    pvrcode,
    clusterKey: clusterKeyFromDeeplink,
  } = guestPlanSelectorData;

  const lineOfBusiness = JSON.parse(
    sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.LOB) || '""'
  ) as string;

  const [isPortalStateReady, setIsPortalStateReady] = useState<boolean>(false);

  const [, setAvailablePlans] = useSessionStorage<any[]>(
    Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS,
    DEFAULT_EMPTY_ARRAY as any
  );

  const { getLocation } = useRouter();
  const [searchParams] = useSearchParams();
  const lob = searchParams?.get('planSelectionLob');
  const coverageType = searchParams?.get('coverageType');
  const flags = useFlags();
  const guestBGApiUrl = getBlueGreenGQLApiUrl(flags, searchParams);

  const [, getPlanDetails] = useGetPlanDefinitions({
    url: guestBGApiUrl,
    onCompleted: (data) => {
      const combinedPlans = getAvailablePlans(data?.data.getPlanDefinitions);
      const filteredPlans = filterAvailablePlansByPlanName(
        combinedPlans,
        planName
      );

      if (filteredPlans.length === 0) {
        // if no plan details found, navigate back to plan selection and remove delsysCode from session storage
        setGuestPlanSelectorData({
          ...guestPlanSelectorData,
          delsysCode: undefined,
        });

        window.location.href = `/guest-plan-selection/plan-selection?planSelectionLob=${lineOfBusiness}&coverageType=${coverageTypeCode}&chipValue=${chipValue}`;
        return;
      }
      const stateCode = JSON.parse(
        sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION) ??
          '{}'
      ).stateCode;
      const planDetails = filteredPlans[0].planDetails[0];
      const planCategory = filteredPlans[0].planCategory;
      // clear available plans to prevent stale data issues
      setAvailablePlans([]);
      setGuestPlanSelectionData({
        selectedPlan: planDetails,
        coverageType: coverageTypeCode,
        lob: lineOfBusiness,
        stateCode,
        planCategory,
        planName,
        policyNumber,
        allConfigs,
        pvrcode,
        clusterKeyFromDeeplink,
      });
      const signInUrl = find(lagoonUIMessage, {
        key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN,
      })?.url;
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.PORTAL_STATE,
        JSON.stringify(
          buildPortalData(
            planDetails,
            lineOfBusiness,
            coverageTypeCode,
            stateCode,
            planDetails?.years?.[0].planYear,
            signInUrl || '',
            planCategory,
            planName,
            policyNumber,
            allConfigs,
            pvrcode,
            clusterKeyFromDeeplink
          )
        )
      );
      setIsPortalStateReady(true);
    },
    onError: (error) => {
      // Error will be handled by useCustomQuery, which will navigate to error-widget for deeplinks
      // eslint-disable-next-line no-console
      console.error('Error fetching plan definitions:', error);
    },
  });

  const routes: GuestRoute[] = getGuestRoutes(guestData);

  useEffect(() => setupPortalStateResponder(), []);

  // Add event listener for navigation requests from nested parcels
  useEffect(() => {
    const handleGuestNavigation = (event) => {
      const { path, params } = event.detail;
      const queryString = new URLSearchParams(params).toString();
      const fullPath = `${path}${queryString ? `?${queryString}` : ''}`;
      navigate(fullPath);
    };

    window.addEventListener('guestNavigationRequest', handleGuestNavigation);

    // Cleanup event listener
    return () => {
      window.removeEventListener(
        'guestNavigationRequest',
        handleGuestNavigation
      );
    };
  }, [navigate]);
  const isLawwGuestExperience = isLawwGuest();
  const lagoonUIMessage = useLagoon('ui-messaging')();
  const allConfigs = useLagoon('config')();
  const typeOfCareText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.TYPE_OF_CARE,
  })?.message;
  const typeOfMedicalCoverageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.TYPE_OF_MEDICAL_COVERAGE,
  })?.message;
  const planSelection = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.PLAN_SELECTION,
  })?.message;
  const yearSelection = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.YEAR_SELECTION,
  })?.message;
  const location = getLocation();
  const acquisitionData = useLagoon('acquisition')?.();
  const portalSourceFromLagoon = find(acquisitionData, {
    clientCode: requestData?.client || '',
  })?.portalSource;
  const shouldShowTypeOfCareCrumb = isGuestDeeplinkFlow
    ? !coverageTypeCode
    : !isLawwGuestExperience;
  const isOnPlanYearSelection = location.pathname === '/plan-year-selection';
  const url = window.location.href;
  const isFindcareLoaded = url.includes('/browse');

  const educationalContentStore = useStore(
    StoreKeys.EDUCATIONAL_CONTENT_STATUS
  );

  const setEducationalContentStore = useStore(
    StoreKeys.SET_EDUCATIONAL_CONTENT_STATUS
  );

  const [educationalContent, getEducationalContentStatus] =
    useEducationalContentStatus({
      url: guestBGApiUrl,
    });

  const [saveEducationalContent] = useSaveEducationalContentStatus({
    url: guestBGApiUrl,
  });

  const heightOrNoHeight = isFindcareLoaded ? {} : { minHeight: '100vh' };
  const heightOrMinHeight = isLawwGuestExperience
    ? heightOrNoHeight
    : { height: '100vh' };

  useEffect(() => {
    if (isEmpty(educationalContentStore)) {
      getEducationalContentStatus({
        variables: {},
      }).then(() => {
        if (educationalContent?.data?.educationalContentStatus) {
          setEducationalContentStore(
            educationalContent.data.educationalContentStatus
          );
        }
      });
    }
  }, [educationalContent?.data?.educationalContentStatus]);

  // Update local store when mutation completes
  useEffect(() => {
    if (saveEducationalContent?.data?.saveEducationalContentStatus) {
      setEducationalContentStore(
        saveEducationalContent.data.saveEducationalContentStatus
      );
    }
  }, [saveEducationalContent?.data, setEducationalContentStore]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll content area back to top whenever the guest route changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  const isErrorPage = window.location.pathname.includes('/unable-to-load-plan');

  useEffect(() => {
    // Skip API call if on error page
    if (isErrorPage) {
      setIsPortalStateReady(true);
      return;
    }

    const currentYear = String(
      DateTime.getCurrentDateTimeWithCentralOffset().getFullYear()
    );

    if (
      isGuestDeeplinkFlow &&
      lineOfBusiness &&
      coverageTypeCode &&
      delsysCode &&
      !isLawwGuestExperience
    ) {
      getPlanDetails({
        variables: {
          planYear: planYear || currentYear,
          lob: lineOfBusiness,
          coverageType: coverageTypeCode,
          isDualYear: true,
          isEI: lineOfBusiness === 'EI',
          planIdentifier: delsysCode,
          portalSource: portalSourceFromLagoon ?? 'psx',
          source,
        },
      });
    } else {
      setIsPortalStateReady(true);
    }
  }, [
    lineOfBusiness,
    coverageTypeCode,
    delsysCode,
    planYear,
    isErrorPage,
    isGuestDeeplinkFlow,
  ]);

  if (!isPortalStateReady) {
    return <LoadingSpinnerComp isLoading={true} />;
  }

  const shouldHideOverflow = isFindcareLoaded && isLawwGuestExperience;

  return (
    <PageBody
      css={{
        'abyss-page-body-container': {
          backgroundColor: '$pageBodyBgColor',
          maxWidth: '100% !important',
          position: 'relative',
          padding: '0px !important',
          display: 'flex',
          flexDirection: 'column',
          ...(!shouldHideOverflow && { overflow: 'hidden' }),
          ...heightOrMinHeight,
        },
      }}
      id="main-content"
      tabIndex={-1}
    >
      <GuestContentWrapper cssProps={{ shouldHideOverflow }}>
        {!isFindcareLoaded && !isLawwGuestExperience && <GuestHeader />}
        <RoutesScrollableWrapper
          cssProps={{ shouldHideOverflow }}
          ref={scrollContainerRef}
        >
          {lob && (
            <GuestHeaderWrapper backgroundColor={'$bgHeaderColor'}>
              <Flex css={{ width: '100%', justifyContent: 'center' }}>
                <Flex
                  css={{
                    flexDirection: 'column',
                    maxWidth: '1200px',
                    width: '100%',
                  }}
                >
                  <Breadcrumbs
                    css={{
                      'abyss-breadcrumb-current': {
                        fontWeight: 400,
                      },
                    }}
                    data-testid="guest-breadcrumb-link"
                    items={
                      isNavigatingMaestro
                        ? []
                        : [
                            ...(!lineOfBusiness
                              ? [
                                  {
                                    href: '/',
                                    title: typeOfMedicalCoverageText,
                                  },
                                ]
                              : []),
                            ...(shouldShowTypeOfCareCrumb
                              ? [
                                  {
                                    href: `/select-coverage-type?planSelectionLob=${lob}`,
                                    title: typeOfCareText,
                                  },
                                ]
                              : []),
                            ...(coverageType && !planName
                              ? [
                                  {
                                    href: `/plan-selection?planSelectionLob=${lob}&coverageType=${coverageType}`,
                                    title: planSelection,
                                  },
                                ]
                              : []),
                            ...(isOnPlanYearSelection
                              ? [
                                  {
                                    href: `/plan-year-selection?planSelectionLob=${lob}&coverageType=${coverageType}`,
                                    title: yearSelection,
                                  },
                                ]
                              : []),
                          ]
                    }
                  />
                  <div style={{ position: 'relative' }}>
                    <div
                      id="coachmark-anchor"
                      style={{ display: 'inline-block', maxWidth: '100%' }}
                    >
                      <Heading
                        css={{ marginTop: '8px' }}
                        data-testid="guest-choose-location-heading"
                        level={2}
                        size="lg"
                      >
                        {chooseType}
                      </Heading>
                    </div>
                  </div>
                </Flex>
              </Flex>
            </GuestHeaderWrapper>
          )}
          <div style={{ flex: 1 }}>
            <Suspense fallback={null}>
              <Router.Routes>
                <Router.Route>
                  {routes.map((route) => (
                    <Router.Route
                      element={<route.element />}
                      key={route.path}
                      path={route.path}
                    />
                  ))}
                </Router.Route>
              </Router.Routes>
            </Suspense>
          </div>
          {!isFindcareLoaded && (
            <GuestFooterWrapper>
              {!isLawwGuestExperience && (
                <>
                  <CardWrapper backgroundColor={'$infoFooterBackground'}>
                    <Footer />
                  </CardWrapper>
                  <GuestFooter />
                </>
              )}
            </GuestFooterWrapper>
          )}
        </RoutesScrollableWrapper>
      </GuestContentWrapper>
    </PageBody>
  );
};
